import { Geogroup, Ride, RideService, useCancellablePromise } from '@geovelo-frontends/commons';
import { Box, ButtonBase, Pagination, Typography } from '@mui/material';
import { Link } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import RideCard from '../../components/ride-card';

const pageSize = 10;

function Rides({
  geogroup,
  setTab,
}: {
  geogroup: Geogroup | undefined;
  setTab: (tab: 'profile') => void;
}): React.JSX.Element {
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState<number>();
  const [rides, setRides] = useState<Ride[]>();
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();

  useEffect(() => {
    if (geogroup?.partner) getRides(geogroup.partner.id);

    return () => {
      cancelPromises();
      setRides(undefined);
    };
  }, [geogroup, page]);

  async function getRides(partnerId: number) {
    try {
      const { count, rides } = await cancellablePromise(
        RideService.getRides({
          ordering: '-id',
          page,
          pageSize: pageSize,
          partnerIds: [partnerId],
          query:
            '{id, title, geo_point_a, geo_point_a_title, geo_point_b, geo_point_b_title, duration, route_duration, distance, vertical_gain, difficulty, photos{thumbnail, thumbnailSquare}, themes, partners{id, title, code, icon}}',
        }),
      );

      if (count === 0) {
        setTab('profile');
      } else {
        setPagesCount(Math.ceil(count / pageSize));
        setRides(rides);
      }
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.rides.server_error'), { variant: 'error' });
      }
    }
  }

  return (
    <>
      <Box display="flex" flexDirection="column" gap={4}>
        <Typography component="h2" fontWeight={700} variant="h6">
          {t('geovelo.navigation.rides')}
        </Typography>
        <Box display="flex" flexDirection="column" gap={3}>
          {rides
            ? rides.map((ride) => (
                <ButtonBase
                  component={Link}
                  key={ride.id}
                  sx={{ borderRadius: 4 }}
                  to={`/${currentLanguage}/rides/${ride.id}`}
                >
                  <RideCard outlined row key={ride.id} ride={ride} />
                </ButtonBase>
              ))
            : [0, 1, 2].map((key) => <RideCard outlined row key={key} />)}
        </Box>
        <Box alignSelf="center">
          <Pagination count={pagesCount} onChange={(_, page) => setPage(page)} page={page} />
        </Box>
      </Box>
    </>
  );
}

export default Rides;
